/**
 * A company's visual identity, as CSS custom properties.
 *
 * WHAT THIS FIXES. New Leaf's palette was the platform's palette:
 *
 *   * `tenants.primary_color` DEFAULTS to '#4A7C59' — New Leaf's green — so
 *     every company created since multi-tenancy shipped carries New Leaf's exact
 *     three colours because nobody ever typed anything.
 *   * `app.css` hardcoded that same palette as `--brand-warm` / `--brand-cool`,
 *     so every tenant's marketing site and admin console RENDERED in it.
 *   * `root.tsx` hardcoded `theme-color: #4A7C59`, so the browser chrome on a
 *     customer's own phone was New Leaf green.
 *
 * THE RULE: a tenant's colours come from its OWN row. When it has not chosen,
 * fall back to the PLATFORM's neutral palette — never to New Leaf's. New Leaf is
 * a tenant like any other and gets its green from its own row (migration 0092
 * writes it explicitly and stamps `branding_set_at`).
 *
 * ⚠️ `branding_set_at IS NULL` means "never chose", and it is the only way to
 * tell a company that deliberately picked New-Leaf-green from one that merely
 * inherited it from a column default. Migration 0092 cleared the inherited ones.
 */

import { deriveConsoleRamp, type ConsoleRamp } from '@shared/color/console';
import { PLATFORM_THEME, resolveTheme, type TenantTheme, type ThemeInput } from '@shared/color/theme';

/*
 * Re-exported, not redefined. `src/api` serves the identical values to the
 * phone apps through /api/auth/resolve and may not import from `src/app`, so
 * the resolver lives in @shared/color/theme and this file is its front door for
 * everything already importing from `~/lib/theme`.
 */
export { PLATFORM_THEME, resolveTheme, type TenantTheme, type ThemeInput };

export { consoleThemeStyleBlock, CONSOLE_RAMP_VERSION, type ConsoleRamp } from '@shared/color/console';


// Re-exported so the app keeps one import site for "everything theme".
export { FONT_CHOICES, DEFAULT_FONT, isFontKey, fontStylesheetHref, type FontKey } from '@shared/fonts';






/**
 * The `<style>` body that applies a theme, server-rendered into <head>.
 *
 * Overrides the same custom properties `app.css` declares, so every existing
 * rule picks the tenant's colours up with no component changes. Emitted for
 * EVERY tenant including New Leaf — New Leaf's green arrives here from its row
 * like everyone else's, which is what lets the CSS defaults be neutral.
 *
 * Values are hex-validated above, so this cannot inject.
 */
export function themeStyleBlock(theme: TenantTheme): string {
  return [
    ':root{',
    `--brand-cool:${theme.primary};`,
    `--brand-warm:${theme.secondary};`,
    `--brand-primary:${theme.primary};`,
    `--brand-secondary:${theme.secondary};`,
    `--brand-accent:${theme.accent};`,
    '}',
  ].join('');
}

/*
 * THE CONSOLE'S IDENTITY — and why this is not the change that got reverted.
 *
 * This function used to emit `--admin-primary`, `--admin-ink-strong` and
 * `--admin-font-*`, and admin.css read them, so the console's whole palette
 * followed the tenant row. On New Leaf — whose row holds the green #4A7C59 —
 * that repainted the entire admin backend from its warm tan to green, top bar
 * included. Vikelas: "the css for the entire nlc backend is the wrong coloring
 * soooo". It was reverted, and the revert was right.
 *
 * The console has since been asked for again, per-tenant, "the way it works on
 * the website". Both things are true at once, and the difference is not
 * willpower — it is which VALUE lands in which SLOT:
 *
 *   * Then: the RAW customer hex went straight into `--primary` and
 *     `--ink-strong`. Ink and chrome carry meaning, not identity, so an
 *     arbitrary hex there is a redesign nobody reviewed — and, one draft
 *     earlier, pale mint body copy on cream at ~1.1:1.
 *   * Now: `consoleThemeStyleBlock` (below, from @shared/color) emits only
 *     `--id-*` — a DERIVED family with a proven contrast ratio against the
 *     frozen ink and surface tokens, which stay literal. Type on a brand ground
 *     is always one of admin.css's own two inks, never a computed colour, so a
 *     tenant's hex is structurally unable to become text.
 *
 * The namespace is the guard, not a convention. Reusing `--primary` would have
 * been the smaller diff and is exactly how the last one happened: the name
 * carried the authority. `tests/unit/css-tokens-defined.test.ts` asserts the
 * emitter never touches a property admin.css declares for itself.
 */

/**
 * One tenant's derived console identity.
 *
 * Thin wrapper so callers holding a `TenantTheme` do not each have to know
 * which two of its three colours seed a ramp. The maths lives in
 * `@shared/color` because `src/api` serves the same values to the mobile apps
 * and may never import from `src/app`.
 */
export function consoleRampFor(theme: TenantTheme): ConsoleRamp {
  return deriveConsoleRamp(theme.primary, theme.accent);
}

/**
 * Rec. 601 luma, 0–255. The same measure the ink guard uses below.
 */
function luma(hex: string): number {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  return 0.299 * (r ?? 0) + 0.587 * (g ?? 0) + 0.114 * (b ?? 0);
}

/**
 * The darkest a colour may be and still be legible as `--color-bone-warm`
 * (#F7F1E2) text's BACKGROUND, and as type on the bone page. `--color-forest`
 * plays both roles, so its replacement has to satisfy both. #234B36 sits at 62;
 * the platform's #2C2C2C at 44.
 */
const MAX_IDENTITY_LUMA = 110;

/**
 * The SECOND palette. This is the one that was still New Leaf's.
 *
 * `themeStyleBlock` above overrides `app.css`'s `--brand-*` tokens — but the
 * public marketing surface is not painted by those. `marketing.css` declares its
 * own "Forest & Brass" system (`--color-forest: #234B36`, `--color-brass:
 * #B08D57`, `--color-bone: #F4EEE0`), used 69 times for the green alone, and
 * NOTHING was overriding it. So the per-tenant theming shipped, the browser
 * chrome and admin console went neutral, and every tenant's marketing pages
 * carried on rendering in New Leaf's exact green — which is most of what "it
 * looks like two companies in one page" actually meant on ssc.broomstack.app.
 *
 * ⚠️ EMITTED FOR NON-NEW-LEAF TENANTS ONLY. New Leaf reads the CSS defaults, so
 * its live site is byte-identical to before this existed. A remap can only ever
 * make someone else's site not-New-Leaf's; it can never alter New Leaf's own.
 *
 * ⚠️ ONLY THE IDENTITY SLOT IS REMAPPED. `--color-forest` and its family carry a
 * company's colour. `--color-brass`, `--color-sage` and the bone surfaces are
 * left alone: each is load-bearing for contrast in a role the tenant's chosen
 * hex may not fit (brass is a light button ground that sets forest-coloured
 * type ON it), and swapping them blind is how the accent colour nearly became
 * the body-copy ink — see the note at the bottom of this file. Neutralising
 * those properly is a design decision, not a substitution.
 */
export function marketingThemeStyleBlock(theme: TenantTheme): string {
  /*
   * If the company's own primary is too light for the role, use the platform's
   * near-black rather than shipping bone-on-pale text. Their colour still
   * reaches `--brand-primary` via themeStyleBlock; it just does not get to be
   * the thing 69 rules set text on.
   */
  const identity =
    luma(theme.primary) <= MAX_IDENTITY_LUMA ? theme.primary : PLATFORM_THEME.primary;

  return [
    ':root{',
    `--color-forest:${identity};`,
    `--color-forest-2:${identity};`,
    `--color-moss:${identity};`,
    `--color-primary:${identity};`,
    `--color-ring:${identity};`,
    '}',
  ].join('');
}

/*
 * ⚠️ `--brand-dark` IS DELIBERATELY NOT IN THAT LIST, and must not be added.
 *
 * Despite the name it is not a brand colour — app.css uses it as the page's INK
 * (`body { color: var(--brand-dark) }`, plus headings and payroll totals). An
 * earlier draft mapped `theme.accent` onto it, which for New Leaf meant its
 * accent `#C5D8C7` — pale mint — became the body text colour on a `#FAF8F5`
 * cream page. That is roughly 1.1:1 contrast: the entire site's copy would have
 * been invisible, on the live tenant, from the first deploy.
 *
 * Ink is a legibility decision, not an identity one. A tenant's accent belongs
 * on `--brand-accent`, which nothing sets type in.
 */
